/**
 * The small pieces Board 11's day sheet and its two popups draw, and Board
 * 12's checklist — their numbers from the boards' HTML. Words come from
 * `copy.ts`; colours from the tokens; the weather from Activity's forecast
 * (`activity.md` §6.9 — the branch office's National Weather Service
 * forecast, do-not-substitute 15), drawn with Activity's own sky glyphs so a
 * sun on the calendar and a sun on a visit day are one sun.
 */
import type { ReactNode } from 'react'
import { RAIN_BLUE_FROM, WEATHER_COLOURS, WEATHER_TITLES, type DayForecast, type WeatherIcon } from '@rowted/shared'
import { cn } from '../lib/cn.js'
import { Glyph } from '../activity/glyphs.js'
import { VisitGlyph } from './glyphs.js'

/** The forecast's sky in its own colour — 15 on the day sheet and the Move band, 12 on Add a day's strip. */
export function Sky({ icon, size, stroke = 1.9 }: { icon: WeatherIcon; size: number; stroke?: number }) {
  return (
    <span className="inline-flex" style={{ color: WEATHER_COLOURS[icon] }}>
      <Glyph name={icon} size={size} stroke={stroke} title={WEATHER_TITLES[icon]} />
    </span>
  )
}

/**
 * The day sheet's forecast (Board 11 A): the sky, its word (*Partly sunny*),
 * the high bold, the low faint, a drop and the chance of rain — blue from 30 %.
 */
export function DayForecastLine({ forecast }: { forecast: DayForecast }) {
  return (
    <span className="inline-flex items-center gap-[5px] whitespace-nowrap text-[12px] leading-none">
      <Sky icon={forecast.icon} size={15} />
      <span className="font-semibold text-text-body">{WEATHER_TITLES[forecast.icon]}</span>
      {forecast.high !== null && <b className="font-bold text-text">{forecast.high}°</b>}
      {forecast.low !== null && <span className="-ml-0.5 font-semibold text-text-faint">{forecast.low}°</span>}
      <span
        className={cn('inline-flex items-center gap-0.5 font-semibold',
          forecast.rain >= RAIN_BLUE_FROM ? 'text-info' : 'text-text-faint')}
      >
        <Glyph name="drop" size={11} stroke={2.2} />
        {forecast.rain}%
      </span>
    </span>
  )
}

/**
 * A section of the day sheet (Board 11 A) — the 10.5 uppercase label, 16
 * above and 8 below, and on its right the green link that goes to the tab
 * (*› Time tab*, *› Materials*, *› See all*).
 */
export function SheetSection({ title, link, children, className, asDrawn }: {
  title: string
  link?: { label: string; onClick: () => void }
  children: ReactNode
  className?: string
  /** `data-as-drawn` — Board 18 F draws the CLOSE-OUT section on its own. */
  asDrawn?: string
}) {
  return (
    <section className={className} data-as-drawn={asDrawn}>
      <div className="mb-2 mt-4 flex items-center text-[10.5px] font-semibold tracking-[.06em] text-text-faint">
        <h3 className="m-0 text-[10.5px] font-semibold uppercase">{title}</h3>
        <span className="flex-1" />
        {link && (
          <button
            type="button"
            onClick={link.onClick}
            className="inline-flex items-center gap-1 whitespace-nowrap text-[12.5px] font-semibold tracking-normal
                       text-accent hover:text-accent-dark"
          >
            <VisitGlyph name="chevronRight" size={13} stroke={2.2} />
            {link.label}
          </button>
        )}
      </div>
      {children}
    </section>
  )
}

/** A label and its value on one line (Board 11 A's *Planned*, *On site*, *Loaded*): the label 110 wide, muted. */
export function SheetRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex gap-2.5 py-1 text-[13px]">
      <span className="w-[110px] shrink-0 text-text-muted">{label}</span>
      <span className="min-w-0 flex-1">{children}</span>
    </div>
  )
}

/**
 * A 22px chip with its 6px dot (Board 11 C's *Call the vendor* · *Moves
 * itself* · *Edit the text*) — warn, green or grey; never black.
 */
export type TagChipTone = 'warn' | 'green' | 'grey'

const TAG_CHIP_TONES: Record<TagChipTone, string> = {
  warn: 'bg-warn-bg text-warn',
  green: 'bg-accent-soft text-accent-dark',
  grey: 'bg-line-soft text-text-muted',
}

export function TagChip({ tone, children, onClick, pressed }: {
  tone: TagChipTone
  children: ReactNode
  /** *Edit the text* is a chip that does something. */
  onClick?: () => void
  pressed?: boolean
}) {
  const look = cn('inline-flex h-[22px] shrink-0 items-center gap-[5px] whitespace-nowrap rounded-full px-2',
    'text-[11.5px] font-semibold', TAG_CHIP_TONES[tone])
  const dot = <i aria-hidden="true" className="inline-block h-1.5 w-1.5 rounded-full bg-current" />
  if (!onClick) return <span className={look}>{dot}{children}</span>
  return (
    <button type="button" onClick={onClick} aria-pressed={pressed} className={cn(look, 'hover:brightness-95')}>
      {dot}{children}
    </button>
  )
}

/**
 * Board 12's mark for a line of the checklist — a 22px green circle with a
 * white tick when it passes, the amber triangle in a warn ring when it is
 * still open.
 */
export function CheckMark({ pass }: { pass: boolean }) {
  return pass
    ? (
      <span className="inline-flex h-[22px] w-[22px] shrink-0 items-center justify-center rounded-full bg-accent text-white">
        <VisitGlyph name="check" size={12} stroke={3} />
      </span>
    )
    : (
      <span
        className="inline-flex h-[22px] w-[22px] shrink-0 items-center justify-center rounded-full border-[1.5px]
                   border-warn-line bg-warn-bg text-warn"
      >
        <VisitGlyph name="warn" size={12} stroke={2.4} />
      </span>
    )
}
